import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@/components/ui/modal";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { useAPIRsapub } from "@/hooks/use-api-rsapub";
import { useAPIEmailLogin } from "@/hooks/use-api-email-login";
import { iamEmailManager } from "@/manager";
import { encryptRSAWithPub } from "@/utils/rsa-pkcs1-encrypt";
import analytics from "@/utils/analytics";

export function ActivateSection() {
  const { t } = useTranslation();
  const [inviationCode, setInvitationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [sendInvitationCodeCount, setSendInvitationCodeCount] = useState(0);
  const invitationCodeRef = useRef<HTMLInputElement>(null);
  const blockTimer = useRef(null);
  const navigate = useI18nNavigate();
  const { handleAPIRsapub } = useAPIRsapub();
  const { handleInvitationLogin } = useAPIEmailLogin();

  // 发送邀请码
  const sendInvitationCode = async (force = false) => {
    if (sendInvitationCodeCount >= 10 && !force) {
      setError(t("invite.activate_section.requests_too_frequent"));
      blockTimer.current = setTimeout(() => {
        setSendInvitationCodeCount(0);
        setError("");
      }, 60000);
      return;
    }
    analytics.trackInvitationCode({ email: iamEmailManager.get() });
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const { rsaId, module, exponent } = await handleAPIRsapub();
      const activationCode = encryptRSAWithPub(module, exponent, inviationCode);
      await handleInvitationLogin(rsaId, activationCode);
      navigate("/");
    } catch (error) {
      analytics.trackInvitationCodeFail({
        reason: error.message,
        email: iamEmailManager.get(),
      });
      setSendInvitationCodeCount((c) => c + 1);
      setLoading(false);
      setError(error.message);
      requestAnimationFrame(() => {
        invitationCodeRef.current?.focus();
      });
      return;
    }
    analytics.trackInvitationCodeSuccess({ email: iamEmailManager.get() });
    setSendInvitationCodeCount((c) => c + 1);
    setInfo(t("invite.activate_section.activate_success"));
    setLoading(false);
  };

  // 修改邀请码
  const input = (v: string) => {
    setInvitationCode(v);
  };

  return (
    <form className="w-full flex flex-col items-center transition-all duration-500">
      {/* 邀请码输入 */}
      <div className="relative w-full mb-10">
        <input
          ref={invitationCodeRef}
          type="text"
          autoFocus
          className="w-full mb-4 bg-gray-800 border border-gray-700 rounded-lg pl-4 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-i-primary focus:border-transparent transition-all"
          aria-label={t("invite.activate_section.invitation_code")}
          placeholder={t("invite.activate_section.enter_invitation_code")}
          value={inviationCode}
          onChange={(e) => input(e.target.value)}
          disabled={loading}
        />

        {/* 错误/提示 */}
        {error && (
          <div className="w-full text-sm text-center text-red-400">{error}</div>
        )}
        {info && (
          <div className="w-full text-sm text-center text-green-400">
            {info}
          </div>
        )}
      </div>

      <button
        type="submit"
        className={`w-full py-3 rounded-lg text-white text-base font-medium transition-all flex items-center justify-center ${
          inviationCode.length
            ? "bg-i-primary hover:bg-i-primary-hover"
            : "bg-i-placeholder cursor-not-allowed"
        }`}
        disabled={!inviationCode.length || loading}
        onClick={(e) => {
          e.preventDefault();
          sendInvitationCode();
        }}
      >
        {loading ? (
          <Spinner size={20} className="mr-2" />
        ) : (
          t("invite.activate_section.get_started")
        )}
      </button>
    </form>
  );
}
